<template>
  <voicemod-key-layout>
    <div class="address-form-view">
      <img
        class="address-form-view__logo"
        src="./assets/keyLogo.svg"
        alt="key logo"
      >
      <h1 class="headline-md">
        {{ $t('vmKeyAddressForm.title') }}
      </h1>
      <span class="body-sm">
        {{ $t('vmKeyAddressForm.subtitle') }}
      </span>
      <HYForm
        v-model="formData"
        show-empty-fields
        @changed="checkEmptyForm"
      >
        <HYInput
          id="name"
          name="name"
          :label="$t('vmKeyAddressForm.inputs.name')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.name')"
          :external-value="formData.name"
          :has-error="!!formErrors.name"
          :error-message="errorMessages[formErrors.name]"
        />
        <HYInput
          id="addressLines"
          name="addressLines"
          :label="$t('vmKeyAddressForm.inputs.address')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.address')"
          :external-value="formData.addressLines"
          :has-error="!!formErrors.addressLines"
          :error-message="errorMessages[formErrors.addressLines]"
        />
        <HYInput
          id="city"
          name="city"
          :label="$t('vmKeyAddressForm.inputs.city')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.city')"
          :external-value="formData.city"
          :has-error="!!formErrors.city"
          :error-message="errorMessages[formErrors.city]"
        />
        <HYInput
          id="state"
          name="state"
          :label="$t('vmKeyAddressForm.inputs.state')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.state')"
          :external-value="formData.state"
          :has-error="!!formErrors.state"
          :error-message="errorMessages[formErrors.state]"
        />
        <HYInput
          id="postalCode"
          name="postalCode"
          :label="$t('vmKeyAddressForm.inputs.zipCode')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.zipCode')"
          :external-value="formData.postalCode"
          :has-error="!!formErrors.postalCode"
          :error-message="errorMessages[formErrors.postalCode]"
        />
        <HYInput
          id="country"
          name="country"
          :label="$t('vmKeyAddressForm.inputs.country')"
          type="text"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.country')"
          :external-value="formData.country || 'United States'"
          :disabled="true"
        />
        <HYInput
          id="phone"
          name="phone"
          :label="$t('vmKeyAddressForm.inputs.phone')"
          type="tel"
          variant="default"
          :placeholder="$t('vmKeyAddressForm.inputs.phone')"
          :external-value="formData.phone"
          :has-error="!!formErrors.phone"
          :error-message="errorMessages[formErrors.phone]"
        />
        <base-button
          v-if="!hasBeenValidated"
          :customizable="{ maxWidth: '350px' }"
          :is-type="['simple', 'vanish']"
          @click.native="validateAddress(true)"
        >
          {{ $t('vmKeyAddressForm.buttons.checkAddress') }}
        </base-button>
        <div
          v-else
          class="address-form-view__check-address-container"
        >
          <base-button
            :customizable="{ maxWidth: '350px' }"
            :is-type="['simple', 'social']"
            @click.native="validateAddress()"
          >
            {{ $t('vmKeyAddressForm.buttons.validateAgain') }}
          </base-button>
          <base-button
            class="address-form-view__continue-button"
            :customizable="{ maxWidth: '350px' }"
            :is-type="['simple', 'vanish']"
            @click.native="handleContinue"
          >
            {{ $t('vmKeyAddressForm.buttons.continue') }}
          </base-button>
        </div>
        <div class="address-form-view__private-policy-container">
          <span class="address-form-view__private-policy body-xs">
            {{ $t('vmKeyAddressForm.privatePolicy') }}
          </span>
          <a
            href="https://www.voicemod.net/privacy/"
            class="interactive-xs"
          >
            {{ $t('vmKeyAddressForm.privatePolicyLink') }}
          </a>
        </div>
      </HYForm>
    </div>
  </voicemod-key-layout>
</template>
<script>
import VoicemodKeyLayout from '@/layouts/VoicemodKeyLayout.vue';
import HYInput from '@/components/HYInput/HYInput.vue';
import HYForm from '@/components/HYForm/HYForm.vue';
import BaseButton from '@RepoComponent/BaseButton/BaseButton';
import { AddressValidationClient } from '@/services/models/addressValidation/addressValidation';
import { CookieService } from '@/services/cookie';
import { ROUTER_PATHS } from '@/router/paths';
import { metricsService } from  '@/metrics/metrics';
import { mParticleMetric } from '../../metrics/mparticle';
import SessionValidation from '../../mixins/SessionValidation/SessionValidation';
import { LOGIN_ACTIONS_CONSTANTS } from '../../domain/login/constants';

export default {
    name: 'AddressFormView',
    components: {
        VoicemodKeyLayout,
        HYInput,
        BaseButton,
        HYForm,
    },
    mixins: [SessionValidation(LOGIN_ACTIONS_CONSTANTS.VM_KEY)],
    data() {
        return {
            formErrors: {
                name: '',
                phone: '',
                addressLines: '',
                city: '',
                state: '',
                postalCode: '',
            },
            errorMessages: {
                emptyField: this.$t('vmKeyAddressForm.errors.emptyField'),
                incorrectInfo: this.$t('vmKeyAddressForm.errors.incorrectInfo'),
            },
            userId: '',
            formattedAddress: '',
            usPsData: {},
            hasBeenValidated: false,
        };
    },
    computed: {
        isFormValid() {
            return Object.values(this.formErrors).every((error) => error === '');
        },
        formData: {
            get() {
                return this.$store.state.vmkey_store.formData ?? {};
            },
            set(newValue) {
                this.$store.dispatch('vmkey_store/updateFormData', newValue);
            },
        },
        planType() {
            return this.$route.query.isVMKeySub === 'true' ? 'free' : 'pro';
        },
    },
    mounted() {
        this.userId = CookieService.getCookie('userId');
        this.trackViewVMKeyAddressForm();
    },
    methods: {
        trackViewVMKeyAddressForm() {
            const eventData = {
                product_name: this.planType === 'free' ? 'vm-key-free' : 'vm-key-pro',
                product_id: this.$route.query.productId,
                original_voicemod_system: 'vm-mobile',
                page_url_full_path: window.location.hash.slice(1),
            };
            metricsService.trackEvent(eventData);
            mParticleMetric.track(
                mParticleMetric.EVENTS.VIEW_VM_KEY_ADDRESS_FORM,
                {
                    page_url_full_path: eventData.page_url_full_path,
                },
                mParticleMetric.EVENT_TYPE.Navigation,
            );
        },
        isFormFilled() {
            return Object.values(this.formData).every((value) => !!value);
        },
        getPostalCode() {
            if (this.usPsData.zipCodeExtension) {
                return `${this.usPsData.zipCode}-${this.usPsData.zipCodeExtension}`;
            }

            return this.usPsData.zipCode;
        },
        async handleContinue() {
            if (!this.isFormFilled()) {
                this.checkEmptyForm(true);
                return;
            }

            this.$router.push({
                path: ROUTER_PATHS.VM_KEY_COMPLETE_ORDER,
                query: {
                    ...this.$route.query,
                    is_vm_key_subscription: 'true',
                    userAddress: JSON.stringify(this.formData),
                },
            });
        },
        getFormattedAddress({result}) {
            return result?.address?.formattedAddress ?? '';
        },
        getUsPsData({result}) {
            return result?.uspsData?.standardizedAddress ?? {};
        },
        checkEmptyForm(isForced = false) {
            if (!isForced) {
                return;
            }

            Object.keys(this.formData).forEach((key) => {
                if (this.formData[key] === '' || this.formData[key] === null) {
                    this.formErrors[key] = 'emptyField';
                    return;
                }

                this.formErrors[key] = '';
            });
        },
        checkUsPsIndividualFields(uspsField, formField) {
            const updatableData = JSON.parse(JSON.stringify(this.formData));
            if (!this.usPsData[uspsField]) {
                this.formErrors[formField] = 'incorrectInfo';
                return;
            }

            const isSame = this.usPsData[uspsField].toLowerCase() === updatableData[formField].toLowerCase();
            this.formErrors[formField] = !isSame ? 'incorrectInfo' : '';

            const uspsFieldValue = this.usPsData[uspsField] ?? updatableData[formField];
            updatableData[formField] = !isSame ? uspsFieldValue : updatableData[formField];
            this.$store.dispatch('vmkey_store/updateFormData', updatableData);
        },
        checkUsPsData(comeFromContinue = false) {
            this.checkUsPsIndividualFields('zipCode', 'postalCode');
            this.checkUsPsIndividualFields('firstAddressLine', 'addressLines');
            this.checkUsPsIndividualFields('city', 'city');
            this.checkUsPsIndividualFields('state', 'state');

            if (this.isFormValid && comeFromContinue) {
                this.handleContinue();
            }
        },
        async validateAddress(comeFromContinue = false) {
            this.checkEmptyForm(true);

            if (!this.isFormFilled()) {
                return;
            }

            this.hasBeenValidated = true;
            try {
                const response = await AddressValidationClient.validateAddress(this.userId, this.formData);
                if (response) {
                    this.formattedAddress = this.getFormattedAddress(response);
                    this.usPsData = this.getUsPsData(response);
                }
                this.checkUsPsData(comeFromContinue);
            } catch (error) {
                console.error('error', error);
            }
        },
    },
};
</script>

<style src="./AddressFormView.scss" lang="scss"></style>